import { useCallback, useState } from 'react';
import { extensionStoreUrl } from '@heybesafe/shared';
import { useSafe } from '../context/SafeContext';
import { useRecoveryOptional } from '../context/RecoveryContext';
import {
  isOnboardingActive,
  hasSeenWelcome,
  markWelcomeSeen as persistWelcomeSeen,
  isChecklistDismissed,
  dismissChecklist as persistDismiss,
  isExtensionMarkedInstalled,
  markExtensionInstalled as persistExtensionInstalled,
} from './storage';
import { useExtensionDetected } from './extensionDetection';

export type OnboardingStepId =
  | 'installExtension'
  | 'import'
  | 'family'
  | 'recovery';

export interface OnboardingStep {
  id: OnboardingStepId;
  title: string;
  description: string;
  done: boolean;
  /** Button label for the step's primary action. */
  cta: string;
  /** Internal route to navigate to, when the action is in-app. */
  to?: string;
  /** External link (e.g. the extension store), when the action leaves the app. */
  href?: string;
}

export interface OnboardingState {
  /** Checklist should render: onboarding is active, not dismissed, not all done. */
  visible: boolean;
  /** Welcome modal should render: active and not yet shown. */
  showWelcome: boolean;
  steps: OnboardingStep[];
  completedCount: number;
  total: number;
  allDone: boolean;
  markWelcomeSeen: () => void;
  dismiss: () => void;
  /** "Skip for now" from the welcome modal — dismisses onboarding outright. */
  skipAll: () => void;
  /**
   * Manual fallback for the extension step: user confirms they installed it.
   * Normally the step auto-completes via the presence beacon (see
   * `extensionDetection`); this covers browsers where detection can't run.
   */
  markExtensionInstalled: () => void;
}

/**
 * Drives the first-run onboarding: a one-time welcome modal plus a persistent
 * checklist. Step completion is derived from real state (items, orgs, recovery
 * contacts, and — for the extension — the presence beacon it sets on our
 * origin) wherever possible; a manual flag is only the fallback for the
 * extension step. Onboarding is gated on the registration-set `active` flag so
 * existing users never see it.
 */
export function useOnboarding(): OnboardingState {
  const { items, orgs } = useSafe();
  // Optional: onboarding renders inside RecoveryProvider in the app, but
  // SafePage may be rendered without it in isolated tests.
  const outbound = useRecoveryOptional()?.outbound ?? [];

  // Mirror the persisted flags in state so toggling them re-renders consumers.
  const [welcomeSeen, setWelcomeSeen] = useState(hasSeenWelcome);
  const [dismissed, setDismissed] = useState(isChecklistDismissed);
  const [extInstalled, setExtInstalled] = useState(isExtensionMarkedInstalled);
  // Live detection of the browser extension via the presence beacon it sets on
  // our own origin. Auto-completes the step so most users never touch the
  // manual "Mark installed" fallback.
  const extDetected = useExtensionDetected();

  const active = isOnboardingActive();

  const hasImported = items.some((i) => !!i.importSource);

  const steps: OnboardingStep[] = [
    {
      id: 'installExtension',
      title: 'Install the browser extension',
      description: 'Autofill and save logins as you browse, on Chrome, Edge, or Firefox.',
      done: extInstalled || extDetected,
      cta: 'Get the extension',
      href: extensionStoreUrl(),
    },
    {
      id: 'import',
      title: 'Import your existing passwords',
      description: 'Bring everything over from LastPass, Google, or a CSV.',
      done: hasImported,
      cta: 'Open import',
      to: '/import',
    },
    {
      id: 'family',
      title: 'Set up family sharing',
      description: 'Create a family to share logins securely with the people you trust.',
      done: orgs.length > 0,
      cta: 'Create a family',
      to: '/orgs/new',
    },
    {
      id: 'recovery',
      title: 'Set up account recovery',
      description: "Pick a recovery contact so you're never locked out of your safe.",
      done: outbound.length > 0,
      cta: 'Add a recovery contact',
      to: '/settings/recovery-contacts',
    },
  ];

  const completedCount = steps.filter((s) => s.done).length;
  const total = steps.length;
  const allDone = completedCount === total;

  const markWelcomeSeen = useCallback(() => {
    persistWelcomeSeen();
    setWelcomeSeen(true);
  }, []);

  const dismiss = useCallback(() => {
    persistDismiss();
    setDismissed(true);
  }, []);

  const skipAll = useCallback(() => {
    persistWelcomeSeen();
    persistDismiss();
    setWelcomeSeen(true);
    setDismissed(true);
  }, []);

  const markExtensionInstalled = useCallback(() => {
    persistExtensionInstalled();
    setExtInstalled(true);
  }, []);

  return {
    visible: active && !dismissed && !allDone,
    showWelcome: active && !welcomeSeen,
    steps,
    completedCount,
    total,
    allDone,
    markWelcomeSeen,
    dismiss,
    skipAll,
    markExtensionInstalled,
  };
}
